// @owners { team: patients }
import { type Brand, getBrand } from '@alto/design-system';
import { IMG_ALT_TEXTS } from '~shared/constants';

type ImageAltKey = keyof typeof IMG_ALT_TEXTS;

// Alt text for images whose brand asset differs from the original IMG_ALT_TEXTS description.
// Brand-keyed so a future brand slots in as one new entry; kept here rather than in each brand's
// image module so the lookup stays synchronous on web, where the image modules are lazy-loaded.
const ALT_TEXT_OVERRIDES_BY_BRAND: Record<Brand, Partial<Record<ImageAltKey, string>>> = {
  fuzerx: {
    addFamilyMember: 'Photo of a mother hugging her daughter',
    alert: 'Illustration of an X in a red circle',
    addMedication: 'Illustration of a plus sign over a pill bottle',
    alertIssue: 'Illustration of an X in a red circle',
    autoRefill: 'Illustration of a pill bottle with a no-refills symbol',
    autoRefillEmpty: 'Illustration of two circular arrows in a blue circle',
    autoRefillEnrolled: 'Illustration of two circular arrows in a blue circle',
    autoRefillManagement: 'Illustration of two circular arrows in a blue circle',
    bagWithMedications: 'Illustration of a checkmark badge surrounded by confetti',
    // Abstract brand shapes with no content to describe: empty alt marks the image decorative.
    boxRotated: '',
    // Delivery-card icons (libs/shared/src/helpers/order.ts): calendarAngled, deliveryWoman, mail, pickup, shipmentCourier.
    calendarAngled: 'Illustration of a package',
    deliveryCourierWithPatient: 'Photo of a woman laughing',
    deliveryWoman: 'Illustration of a package with a checkmark',
    eIconInsuranceCircleNewYear: 'Illustration of a card with a plus sign',
    eIconInsuranceCircleOutOfDate: 'Illustration of a card with a plus sign',
    eIconPaymentCircle: 'Illustration of a credit card',
    greenCheckmark: 'Illustration of a checkmark in a blue circle',
    helpBubble: 'Illustration of two overlapping chat bubbles',
    location: 'Illustration of two overlapping map pins',
    insuranceCard: 'Illustration of an insurance card with a plus sign',
    mail: 'Illustration of an envelope',
    needleMeds: 'Illustration of a medication vial with a syringe',
    onboardingResume: 'Illustration of overlapping medication containers',
    prescriptions: 'Illustration of overlapping medication containers',
    notification: 'Illustration of two overlapping mobile phones',
    phone: 'Illustration of two overlapping mobile phones',
    onboardingLanding: 'Photo of a woman laughing',
    progynyWelcome: 'Photo of a woman laughing',
    pharmacyInfo: 'Illustration of a document with a checkmark badge',
    pickup: 'Illustration of a shopping bag with a pill bottle',
    pillBottle: 'Illustration of pill bottles',
    prescription: 'Illustration of two overlapping shapes',
    rateShipment: 'Illustration of a package with a checkmark',
    shipmentCourier: 'Illustration of a package',
    syringe: 'Illustration of a play button',
    // Transfer success page swaps the thumbs up for a success checkmark.
    thumbsUp: 'Illustration of a checkmark in a circle',
    transferPrescriptions: 'Illustration of two mirrored shapes overlapping',
    unboxingGuide: 'Illustration of a pill bottle',
  },
};

// Resolves alt text through the same getBrand() call that selects the brand's image set, so the
// description can't drift from the asset actually rendered.
export const getImageAltText = (imageName: ImageAltKey): string =>
  ALT_TEXT_OVERRIDES_BY_BRAND[getBrand()][imageName] ?? IMG_ALT_TEXTS[imageName];
